import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../../i18n'
import { AnchorPicker } from '../AnchorPicker'
import type { ArtefactAnchor } from '../../lib/artefact-anchors'
import { isEmailAddress, MAIL_SUBJECT_MAX, parseProseHref, proseLinkHref, type ProseLink } from '../../lib/link-forms'
import { isLinkUrlAllowed } from './link-url'

type Kind = ProseLink['kind']

export interface ProseLinkDialogProps {
  /** The href of the link under the caret, or '' for a new link. */
  initialHref: string
  /** Offer "Remove link" (the caret is in a link). */
  canRemove: boolean
  /** Offer the Anchor kind — the editor knows its part (room + live doc), so it can list them. */
  anchorKind: boolean
  /** Every anchor of the artefact (lib/artefact-anchors.ts); null while it is being read. */
  anchors: readonly ArtefactAnchor[] | null
  /** Titles of the parts whose anchors could not be read. */
  failed?: readonly string[]
  /** The part open here (its anchor group is "this part"). */
  currentRoom?: string
  /** Apply the link (a valid href: web, mailto or `#anchor-<id>`). */
  onApply: (href: string) => void
  onRemove: () => void
  onClose: () => void
}

/**
 * The prose Link dialog (AB#350; was a URL-only PromptModal): link to a Web page, an E-mail or an
 * Anchor of the artefact — the slides LinkPopover's kinds, minus "slide". Web keeps the URL check
 * it always had (components/editor/link-url.ts: http, https or mailto only — an href reaches every
 * member) and adds `https://` when no scheme is typed; E-mail is an address and an optional subject
 * (`mailto:to?subject=…`); Anchor is the shared picker (components/AnchorPicker.tsx). It opens on the
 * existing link's kind and values. A link to an anchor that no longer exists says so.
 */
export function ProseLinkDialog({ initialHref, canRemove, anchorKind, anchors, failed = [], currentRoom, onApply, onRemove, onClose }: ProseLinkDialogProps) {
  const { t } = useLang()
  const uid = useId()
  const initial = parseProseHref(initialHref)
  // A link to an anchor opened where anchors cannot be listed is shown (and kept) as typed text.
  const start: ProseLink = initial.kind === 'anchor' && !anchorKind ? { kind: 'web', url: initialHref } : initial
  const [kind, setKind] = useState<Kind>(start.kind)
  const [url, setUrl] = useState(start.kind === 'web' ? start.url : '')
  const [to, setTo] = useState(start.kind === 'email' ? start.to : '')
  const [subject, setSubject] = useState(start.kind === 'email' ? start.subject : '')
  const [anchorId, setAnchorId] = useState<string | null>(start.kind === 'anchor' ? start.anchorId : null)
  const [error, setError] = useState<string | null>(null)
  const firstField = useRef<HTMLInputElement | null>(null)
  const dialog = useRef<HTMLDivElement>(null)

  // The focus goes to the dialog's first field, and back to whatever opened it on close.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    // The Anchor kind has no field of ours: its checked radio, then.
    const first = firstField.current ?? dialog.current?.querySelector<HTMLInputElement>('input:checked')
    first?.focus()
    firstField.current?.select()
    return () => prev?.focus()
  }, [])

  const broken = kind === 'anchor' && anchorId !== null && anchors !== null && !anchors.some((a) => a.id === anchorId)

  const submit = (e?: FormEvent): void => {
    e?.preventDefault()
    let link: ProseLink
    if (kind === 'web') {
      if (!url.trim()) return setError(t('lk_invalid_url'))
      link = { kind: 'web', url }
      // Refused, not written: see components/editor/link-url.ts — an href reaches every member.
      if (!isLinkUrlAllowed(proseLinkHref(link))) return setError(t('fmt_link_invalid'))
    } else if (kind === 'email') {
      if (!isEmailAddress(to)) return setError(t('lk_invalid_email'))
      link = { kind: 'email', to, subject: subject.slice(0, MAIL_SUBJECT_MAX) }
    } else {
      if (!anchorId || broken) return setError(t(broken ? 'an_broken' : 'an_pick'))
      link = { kind: 'anchor', anchorId }
    }
    onApply(proseLinkHref(link))
  }

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    onClose()
  }

  const kinds: Array<{ value: Kind; label: string }> = [
    { value: 'web', label: t('lk_web') },
    { value: 'email', label: t('lk_email') },
    ...(anchorKind ? [{ value: 'anchor' as const, label: t('an_link_kind') }] : []),
  ]
  const errorId = `${uid}-error`
  const described = error ? errorId : undefined

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        ref={dialog}
        className="modal prompt-modal prose-link-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="modal-head">
          <h2 id={`${uid}-title`}>{t('fmt_link_title')}</h2>
          <button type="button" className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <form className="modal-body prompt-body prose-link-body" onSubmit={submit} noValidate>
          <fieldset className="prompt-choice prose-link-kinds">
            <legend>{t('lk_kind')}</legend>
            {kinds.map((k) => (
              <label key={k.value}>
                <input
                  type="radio"
                  name={`${uid}-kind`}
                  value={k.value}
                  checked={kind === k.value}
                  onChange={() => {
                    setKind(k.value)
                    setError(null)
                  }}
                />
                {k.label}
              </label>
            ))}
          </fieldset>

          {kind === 'web' && (
            <label className="prompt-label">
              {t('fmt_link_label')}
              <input
                ref={firstField}
                className="prompt-input"
                value={url}
                placeholder={t('fmt_link_ph')}
                spellCheck={false}
                aria-invalid={error ? true : undefined}
                aria-describedby={described}
                onChange={(e) => {
                  setUrl(e.target.value)
                  setError(null)
                }}
              />
            </label>
          )}
          {kind === 'email' && (
            <>
              <label className="prompt-label">
                {t('lk_to')}
                <input
                  ref={firstField}
                  className="prompt-input"
                  type="email"
                  value={to}
                  spellCheck={false}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={described}
                  onChange={(e) => {
                    setTo(e.target.value)
                    setError(null)
                  }}
                />
              </label>
              <label className="prompt-label">
                {t('lk_subject')}
                <input className="prompt-input" value={subject} maxLength={MAIL_SUBJECT_MAX} onChange={(e) => setSubject(e.target.value)} />
              </label>
            </>
          )}
          {kind === 'anchor' && (
            <>
              <AnchorPicker
                anchors={anchors}
                failed={failed}
                value={anchorId}
                currentRoom={currentRoom}
                onPick={(id) => {
                  setAnchorId(id)
                  setError(null)
                }}
              />
              {broken && !error && <p className="prompt-error prose-link-broken">{t('an_broken')}</p>}
            </>
          )}

          {error && (
            <p id={errorId} className="prompt-error" role="alert">
              {error}
            </p>
          )}
          <div className="prompt-acts">
            {canRemove && (
              <button type="button" className="mini no" onClick={onRemove}>
                {t('fmt_link_remove')}
              </button>
            )}
            <button type="button" className="mini ghost" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="submit" className="mini ok prompt-confirm">
              {t('fmt_link_apply')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
